import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PageLoading, EmptyState } from "@/components/shared";
import { ShieldCheck } from "lucide-react";
import api from "@/lib/api";

const CELL = {
  VALID: "bg-emerald-100 text-emerald-700", COMPLIANT: "bg-emerald-100 text-emerald-700",
  EXPIRING_SOON: "bg-amber-100 text-amber-700", REVIEW_REQUIRED: "bg-amber-100 text-amber-700", ACTION_REQUIRED: "bg-amber-100 text-amber-700",
  MISSING: "bg-red-100 text-red-700", EXPIRED: "bg-red-100 text-red-700", NOT_COVERING_ASSIGNMENT: "bg-red-100 text-red-700", CRITICAL: "bg-red-100 text-red-700",
  NOT_PROVIDED: "bg-slate-100 text-slate-500",
  INCOMPLETE: "bg-slate-100 text-slate-500",
};

function Chip({ s, t }) {
  return <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${CELL[s] || "bg-slate-100 text-slate-500"}`}>{t(`sub.cs_${s}`)}</span>;
}

export default function SubCompliance() {
  const { t } = useTranslation();
  const [view, setView] = useState("workers");
  const [workers, setWorkers] = useState(null);
  const [companies, setCompanies] = useState(null);
  useEffect(() => { api.get("/subcon/compliance/workers").then((r) => setWorkers(r.data.workers)); api.get("/subcon/compliance/companies").then((r) => setCompanies(r.data.companies)); }, []);

  return (
    <div data-testid="sub-compliance">
      <div className="mb-5 inline-flex rounded-full border border-slate-200 bg-white p-1">
        <button onClick={() => setView("workers")} data-testid="matrix-workers-btn" className={`rounded-full px-4 py-1.5 text-sm font-medium ${view === "workers" ? "bg-navy text-white" : "text-slate-500"}`}>{t("sub.matWorkers")}</button>
        <button onClick={() => setView("companies")} data-testid="matrix-companies-btn" className={`rounded-full px-4 py-1.5 text-sm font-medium ${view === "companies" ? "bg-navy text-white" : "text-slate-500"}`}>{t("sub.matCompanies")}</button>
      </div>

      {view === "workers" ? (
        workers === null ? <PageLoading /> : workers.length === 0 ? <EmptyState icon={ShieldCheck} title={t("sub.wkEmpty")} /> : (
          <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
            <table className="w-full text-sm" data-testid="worker-matrix">
              <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-400">
                <tr><th className="px-4 py-3">{t("sub.wkName")}</th><th className="px-4 py-3">{t("sub.wkSub")}</th><th className="px-4 py-3">{t("sub.tSites")}</th><th className="px-4 py-3">{t("sub.dt_PASSPORT")}</th><th className="px-4 py-3">A1</th><th className="px-4 py-3">{t("sub.colAssignment")}</th><th className="px-4 py-3">{t("sub.colOverall")}</th></tr>
              </thead>
              <tbody>
                {workers.map((w) => (
                  <tr key={w.worker_id} className="border-b border-slate-50" data-testid={`matrix-worker-${w.worker_id}`}>
                    <td className="px-4 py-3 font-semibold text-navy">{w.name}</td>
                    <td className="px-4 py-3 text-slate-600">{w.subcontractor_name}</td>
                    <td className="px-4 py-3 text-slate-600">{w.site_name || "—"}</td>
                    <td className="px-4 py-3"><Chip s={w.passport_status} t={t} /></td>
                    <td className="px-4 py-3"><Chip s={w.a1_status} t={t} />{w.a1_valid_until && <div className="mt-0.5 text-[10px] text-slate-400">{w.a1_valid_until}</div>}</td>
                    <td className="px-4 py-3"><Chip s={w.assignment_status} t={t} /></td>
                    <td className="px-4 py-3"><Chip s={w.overall} t={t} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : (
        companies === null ? <PageLoading /> : companies.length === 0 ? <EmptyState icon={ShieldCheck} title={t("sub.coEmpty")} /> : (
          <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
            <table className="w-full text-sm" data-testid="company-matrix">
              <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-400">
                <tr><th className="px-4 py-3">{t("sub.coName")}</th><th className="px-4 py-3">{t("sub.fCountry")}</th><th className="px-4 py-3">{t("sub.coWorkers")}</th><th className="px-4 py-3">{t("sub.tSites")}</th><th className="px-4 py-3">Freistellung</th><th className="px-4 py-3">{t("sub.colInsurance")}</th><th className="px-4 py-3">Finanzamt</th><th className="px-4 py-3">{t("sub.colOverall")}</th><th className="px-4 py-3">{t("sub.colNextExpiry")}</th></tr>
              </thead>
              <tbody>
                {companies.map((c) => (
                  <tr key={c.id} className="border-b border-slate-50" data-testid={`matrix-company-${c.id}`} title={c.reasons?.join(" · ")}>
                    <td className="px-4 py-3 font-semibold text-navy">{c.name}</td>
                    <td className="px-4 py-3 text-slate-600">{c.country || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{c.workers}</td>
                    <td className="px-4 py-3 text-slate-600">{c.active_sites}</td>
                    <td className="px-4 py-3"><Chip s={c.freistellung_status} t={t} /></td>
                    <td className="px-4 py-3"><Chip s={c.insurance_status} t={t} /></td>
                    <td className="px-4 py-3 text-slate-600">{c.finanzamt || "—"}</td>
                    <td className="px-4 py-3"><Chip s={c.compliance} t={t} /></td>
                    <td className="px-4 py-3 text-slate-600">{c.next_expiry || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
}
